// Opens a URL in a new tab from a click handler that must first await a network
// round-trip (e.g. a Cloud Function that mints a Stripe URL).
//
// window.open() only escapes popup blockers when it runs synchronously inside a
// trusted user gesture. Awaiting first breaks that chain, so we open a blank
// placeholder tab on the click and point it at the real URL once it arrives.
export const openInNewTab = async (getUrl, placeholderText) => {
    // Synchronous — still inside the user gesture. We can't pass 'noopener'
    // here: it makes window.open() return null, and we need the handle.
    const win = window.open('', '_blank')

    // Popup blocked — degrade to a same-tab redirect.
    if (!win || win.closed) {
        window.location.assign(await getUrl())
        return
    }

    if (placeholderText) {
        // about:blank inherits our origin, so writing to it is permitted.
        // placeholderText comes from translations.js, never from user input.
        win.document.write(
            `<!doctype html><html><head><meta charset="utf-8">` +
            `<title>${placeholderText}</title></head>` +
            `<body style="margin:0;display:flex;align-items:center;` +
            `justify-content:center;height:100vh;color:#5b6472;` +
            `font-family:system-ui,-apple-system,'Segoe UI',sans-serif">` +
            `${placeholderText}</body></html>`
        )
        win.document.close()
    }

    let url
    try {
        url = await getUrl()
    } catch (err) {
        // Still same-origin about:blank here, so close() is honoured.
        // Rethrow so the caller can surface its error banner.
        win.close()
        throw err
    }

    // The user closed the placeholder while we waited — respect that rather
    // than hijacking the tab they're still looking at.
    if (win.closed) return

    // Sever the opener BEFORE navigating, while the tab is still same-origin
    // about:blank and both operations are permitted.
    win.opener = null
    // replace() keeps the placeholder out of the new tab's history.
    win.location.replace(url)
}
